/**
 * =====================================================================
 * TEMPLATE PREVIEW MODAL - Admin Dashboard Component
 * Version: V4.53
 * Purpose: Preview PDF template with sample data using react-pdf
 * =====================================================================
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import apiService from '../../services/api';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface Template {
    id: string;
    template_key: string;
    template_type: 'chapter' | 'book';
    display_name: string;
    display_description: string;
}

interface Props {
    open: boolean;
    template: Template;
    onClose: () => void;
}

const TemplatePreviewModal: React.FC<Props> = ({ open, template, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [scale, setScale] = useState<number>(1.0);

    // =====================================================================
    // GENERATE PREVIEW
    // =====================================================================

    const generatePreview = async () => {
        try {
            setLoading(true);
            setError(null);
            setPdfUrl(null);
            setNumPages(0);
            setPageNumber(1);

            // Call API to generate preview from admin service
            const blob = await apiService.downloadBlob(
                `/api/admin/templates/preview/${template.id}`,
                'POST',
                {} // Can optionally provide custom sample data
            );

            console.log('[PDF Preview] Blob received:', {
                type: blob.type,
                size: blob.size,
            });

            // Validate blob type
            if (!blob.type.includes('pdf') && blob.type !== 'application/octet-stream') {
                throw new Error(`Invalid file type: ${blob.type}. Expected application/pdf`);
            }

            // Convert blob to ArrayBuffer, then to Uint8Array for react-pdf
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Check PDF signature (should start with "%PDF-")
            // Use TextDecoder for reliable string conversion
            const signatureBytes = uint8Array.slice(0, 5);
            const signature = new TextDecoder('ascii').decode(signatureBytes);
            console.log('[PDF Preview] PDF signature:', signature, 'Bytes:', Array.from(signatureBytes));

            if (!signature.startsWith('%PDF-')) {
                console.error('[PDF Preview] Invalid PDF signature.');
                console.error('[PDF Preview] Expected: %PDF-, Got:', signature);
                console.error('[PDF Preview] First 20 bytes:', Array.from(uint8Array.slice(0, 20)));
                throw new Error('Invalid PDF file - signature check failed');
            }

            // Create blob URL - react-pdf can use this directly
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);

            console.log('[PDF Preview] PDF loaded successfully:', uint8Array.length, 'bytes');
            console.log('[PDF Preview] Blob URL created:', url);
        } catch (err: any) {
            console.error('[PDF Preview] Error generating preview:', err);
            setError(err.message || 'Failed to generate PDF preview. Please check the template for errors.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            generatePreview();
        }

        // Cleanup: revoke blob URL when modal closes
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [open, template.id]);

    // =====================================================================
    // DOWNLOAD PDF
    // =====================================================================

    const handleDownload = () => {
        if (!pdfUrl) return;

        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${template.template_key}-preview.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // =====================================================================
    // RENDER
    // =====================================================================

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5">Template Preview</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {template.display_name} ({template.template_type})
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={generatePreview}
                            disabled={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                        {pdfUrl && (
                            <Button
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                                variant="contained"
                                size="small"
                            >
                                Download
                            </Button>
                        )}
                        <Button startIcon={<CloseIcon />} onClick={onClose} size="small">
                            Close
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={60} />
                        <Typography variant="body1" color="text.secondary">
                            Generating PDF preview...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This may take a few seconds for the first render.
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            p: 4,
                        }}
                    >
                        <Box sx={{ p: 3, bgcolor: 'error.light', borderRadius: 2, maxWidth: 600 }}>
                            <Typography variant="h6" color="error" gutterBottom>
                                Preview Generation Failed
                            </Typography>
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={generatePreview}
                        >
                            Try Again
                        </Button>
                    </Box>
                )}

                {/* PDF Preview */}
                {pdfUrl && !loading && !error && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* PDF Controls */}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            p: 2,
                            bgcolor: 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            {/* Page Navigation */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    size="small"
                                    startIcon={<NavigateBeforeIcon />}
                                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                                    disabled={pageNumber <= 1}
                                >
                                    Previous
                                </Button>
                                <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
                                    Page {pageNumber} of {numPages}
                                </Typography>
                                <Button
                                    size="small"
                                    endIcon={<NavigateNextIcon />}
                                    onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                                    disabled={pageNumber >= numPages}
                                >
                                    Next
                                </Button>
                            </Box>

                            {/* Zoom Controls */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    size="small"
                                    startIcon={<ZoomOutIcon />}
                                    onClick={() => setScale(prev => Math.max(0.5, prev - 0.25))}
                                    disabled={scale <= 0.5}
                                >
                                    Zoom Out
                                </Button>
                                <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
                                    {Math.round(scale * 100)}%
                                </Typography>
                                <Button
                                    size="small"
                                    endIcon={<ZoomInIcon />}
                                    onClick={() => setScale(prev => Math.min(3, prev + 0.25))}
                                    disabled={scale >= 3}
                                >
                                    Zoom In
                                </Button>
                            </Box>
                        </Box>

                        {/* PDF Viewer */}
                        <Box sx={{
                            flex: 1,
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            bgcolor: '#525659',
                            p: 2
                        }}>
                            <Document
                                file={pdfUrl}
                                onLoadSuccess={({ numPages }) => {
                                    setNumPages(numPages);
                                    setPageNumber(1);
                                    console.log('[PDF Viewer] PDF loaded successfully, pages:', numPages);
                                }}
                                onLoadError={(error) => {
                                    console.error('[PDF Viewer] Error loading PDF:', error);
                                    setError('Failed to load PDF. Please try again.');
                                }}
                                loading={
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 2, color: 'white' }}>
                                            Loading PDF...
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Page
                                    pageNumber={pageNumber}
                                    scale={scale}
                                    loading={
                                        <Box sx={{ p: 4, textAlign: 'center' }}>
                                            <CircularProgress size={40} />
                                        </Box>
                                    }
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                />
                            </Document>
                        </Box>
                    </Box>
                )}

                {/* Info Box */}
                {pdfUrl && !loading && (
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="info.dark">
                            ℹ️ This is a sample preview with placeholder data. Actual PDFs will contain user's chapter/book content.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Template ID: <code>{template.id}</code>
                </Typography>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplatePreviewModal;
